'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    fuseCrossFilial,
    FuseCrossFilialResult,
} from '@/lib/api/catalog/unify-suggestions-api'
import { useState } from 'react'
import { MdMerge, MdWarning } from 'react-icons/md'
import { toast } from 'react-toastify'

/**
 * Página master (sem item de sidebar — só por rota) para disparar a fusão
 * cross-filial das duplicatas reais do catálogo da organização.
 */
export default function CatalogFusionPage() {
    const [preview, setPreview] = useState<FuseCrossFilialResult | null>(null)
    const [loading, setLoading] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [done, setDone] = useState<FuseCrossFilialResult | null>(null)

    const runDryRun = async () => {
        setLoading(true)
        setDone(null)
        try {
            const res = await fuseCrossFilial(true)
            if (res.success && res.result) {
                setPreview(res.result)
            } else {
                toast.error(res.error || 'Falha ao pré-visualizar')
            }
        } catch {
            toast.error('Erro ao pré-visualizar a fusão')
        } finally {
            setLoading(false)
        }
    }

    const runFusion = async () => {
        setLoading(true)
        try {
            const res = await fuseCrossFilial(false)
            if (res.success && res.result) {
                setDone(res.result)
                setPreview(null)
                toast.success(
                    `Fusão concluída: ${res.result.mergedItems} item(ns) fundido(s) em ${res.result.groups} grupo(s)`,
                )
            } else {
                toast.error(res.error || 'Falha na fusão')
            }
        } catch {
            toast.error('Erro ao executar a fusão')
        } finally {
            setLoading(false)
            setConfirmOpen(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-2xl mx-auto">
            <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
                <MdMerge className="text-secondary" /> Fusão de catálogo entre filiais
            </h1>
            <p className="text-sm text-base-content/60 mb-6">
                Funde itens de mesmo nome existentes em filiais diferentes da
                organização num único item compartilhado, preservando relações,
                histórico e preços por filial.
            </p>

            <div className="alert alert-warning text-sm mb-6">
                <MdWarning className="text-lg" />
                <span>
                    Operação de manutenção. Os duplicados são marcados como
                    excluídos (soft delete). Rode a pré-visualização antes e,
                    idealmente, tenha um backup do banco.
                </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <button
                    className="btn btn-outline btn-sm gap-1"
                    onClick={runDryRun}
                    disabled={loading}
                >
                    {loading && !confirmOpen ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : null}
                    Pré-visualizar (dry-run)
                </button>
                <button
                    className="btn btn-secondary btn-sm gap-1"
                    disabled={loading || !preview || preview.mergedItems === 0}
                    onClick={() => setConfirmOpen(true)}
                >
                    <MdMerge /> Executar fusão
                </button>
            </div>

            {preview && (
                <div className="mt-6 bg-base-200 rounded-box p-4">
                    <p className="text-sm">
                        Serão fundidos{' '}
                        <strong>{preview.mergedItems}</strong> item(ns) em{' '}
                        <strong>{preview.groups}</strong> grupo(s) de duplicatas.
                    </p>
                </div>
            )}

            {done && (
                <div className="mt-6 alert alert-success text-sm">
                    <span>
                        Fusão concluída: {done.mergedItems} item(ns) fundido(s)
                        em {done.groups} grupo(s).
                    </span>
                </div>
            )}

            <ConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={runFusion}
                loading={loading}
                title="Confirmar fusão cross-filial"
                message={
                    preview
                        ? `Fundir ${preview.mergedItems} item(ns) em ${preview.groups} grupo(s)? Os duplicados serão soft-deletados e suas relações migradas para o item principal. Não é facilmente reversível.`
                        : ''
                }
                confirmButtonText="Fundir"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn-secondary"
            />
        </div>
    )
}
